import React from 'react';
import { useAuth } from '../hooks/useAuth';
import { useConfig } from '../hooks/useConfig';
import { 
  Users, 
  Car, 
  FileText, 
  Shield, 
  TrendingUp, 
  Clock,
  AlertCircle,
  GitBranch
} from 'lucide-react';
import { Link } from 'react-router-dom';

const StatCard = ({ title, value, icon: Icon, color, trend, styles }: any) => (
  <div 
    className="group bg-card transition-all border shadow-sm flex flex-col space-y-4 rounded-2xl"
    style={{
      padding: styles.padding,
      borderColor: styles.borderColor,
      borderWidth: styles.borderSize,
      backgroundColor: styles.backgroundColor,
    }}
  >
    <div className="flex items-center justify-between">
      <div 
        className={color + " p-2 md:p-3 rounded-xl text-white group-hover:scale-110 transition-transform border"}
        style={{
          borderColor: styles.bordo_icona_colore || '#ffffff',
          borderWidth: styles.bordo_icona_spessore || '1px'
        }}
      >
        <Icon className="w-5 h-5 md:w-6 md:h-6" />
      </div>
      {trend && (
        <span className="text-emerald-500 text-sm font-medium flex items-center">
          <TrendingUp className="w-4 h-4 mr-1" />
          {trend}
        </span>
      )}
    </div>
    <div>
      <p 
        className="text-sm font-medium opacity-80"
        style={{ color: styles.textColor, fontFamily: styles.textFont }}
      >{title}</p>
      <h3 
        className="text-2xl font-bold mt-1"
        style={{ color: styles.textColor, fontFamily: styles.textFont }}
      >{value}</h3>
    </div>
  </div>
);

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { getTileStyles } = useConfig();
  const [versionData, setVersionData] = React.useState({ major: 0, minor: 0, patch: 0 });

  const globalStyles = getTileStyles();
  const automezziStyles = getTileStyles("Automezzi");
  const sociStyles = getTileStyles("Soci");
  const verbaliStyles = getTileStyles("Verbali");

  React.useEffect(() => {
    fetch('/api/version')
      .then(res => res.json())
      .then(data => {
        if (data && !data.error) setVersionData(data);
      })
      .catch(err => console.error('Dashboard: version fetch error:', err));
  }, []);

  const firstName = user?.full_name?.split(' ')[0] || 'Utente';

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Mia Dashboard</h1>
          <p className="text-muted-foreground mt-1">Benvenuto, {firstName}. Ecco cosa sta succedendo oggi nel sistema Gesvol.</p>
        </div>
        <Link 
          to="/user/versione"
          className="flex items-center gap-1.5 px-2.5 py-1 bg-accent/50 hover:bg-accent border rounded-full transition-colors shrink-0 whitespace-nowrap self-start md:self-center min-h-[32px]"
          style={{ borderColor: globalStyles.borderColor }}
        >
          <GitBranch className="w-3.5 h-3.5 text-primary shrink-0" />
          <span className="text-xs font-mono font-bold leading-none">v{versionData.major}.{versionData.minor}.{versionData.patch}</span>
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6" style={{ gap: globalStyles.margin }}>
        <StatCard 
          title="Totale Soci" 
          value="1,248" 
          icon={Users} 
          color="bg-blue-500" 
          trend="+12%" 
          styles={sociStyles}
        />
        <StatCard 
          title="Automezzi Attivi" 
          value="14" 
          icon={Car} 
          color="bg-emerald-500" 
          styles={automezziStyles}
        />
        <StatCard 
          title="Verbali Pendenti" 
          value="23" 
          icon={FileText} 
          color="bg-amber-500" 
          trend="-5%" 
          styles={verbaliStyles}
        />
        <StatCard 
          title="Allarmi Sistema" 
          value="2" 
          icon={AlertCircle} 
          color="bg-rose-500" 
          styles={globalStyles}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" style={{ gap: globalStyles.margin }}>
        <div 
          className="lg:col-span-2 bg-card rounded-2xl border p-6"
          style={{ borderColor: globalStyles.borderColor, borderWidth: globalStyles.borderSize, backgroundColor: globalStyles.backgroundColor }}
        >
          <h3 className="text-lg font-bold mb-4" style={{ color: globalStyles.textColor, fontFamily: globalStyles.textFont }}>Attività Recenti</h3>
          <div className="space-y-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-start space-x-4">
                <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium" style={{ color: globalStyles.textColor, fontFamily: globalStyles.textFont }}>Nuovo socio registrato</p>
                  <p className="text-xs text-muted-foreground">Marco Rossi è stato aggiunto alla Zona Bologna</p>
                </div>
                <span className="text-xs text-muted-foreground">2h fa</span>
              </div>
            ))}
          </div>
        </div>

        <div 
          className="bg-card rounded-2xl border p-6"
          style={{ borderColor: globalStyles.borderColor, borderWidth: globalStyles.borderSize, backgroundColor: globalStyles.backgroundColor }}
        >
          <h3 className="text-lg font-bold mb-4" style={{ color: globalStyles.textColor, fontFamily: globalStyles.textFont }}>Prossime Scadenze</h3>
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
              <p className="text-sm font-bold text-amber-600">Revisione Automezzo</p>
              <p className="text-xs text-amber-600/80 mt-1">Fiat Panda (GEV01) - Scade tra 3 giorni</p>
            </div>
            <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
              <p className="text-sm font-bold text-blue-600">Rinnovo Convenzione</p>
              <p className="text-xs text-blue-600/80 mt-1">Comune di Bologna - Scade tra 15 giorni</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
